import React from 'react';
// @ts-ignore
import Fade from '@stahl.luke/react-reveal/Fade';
import { CheckIcon } from '@heroicons/react/20/solid';
import LandingNavigation from '../LandingNavigation';
import mainScreenBg from '../what-is-nucleo/assets/logoPart.svg';
import Footer from '../Footer';
import { useDocumentTitle } from '../../../helpers/hooks';
import {
  PRICING_MATRIX_ROWS,
  PRICING_PLAN_NAMES,
} from './pricingTableData';

function PricingComparisonTable() {
  return (
    <div className="w-full overflow-x-auto">
      <table className="min-w-[920px] w-full border-collapse text-left text-sm text-neutral-700">
        <caption className="sr-only">
          Nucleo membership plans and feature comparison
        </caption>
        <thead>
          <tr className="bg-main-900 text-white">
            <th
              scope="col"
              className="sticky left-0 z-10 bg-main-900 px-4 py-4 font-semibold text-sm min-w-[200px] border-r border-neutral-100"
            >
              Membership
            </th>
            {PRICING_PLAN_NAMES.map((plan) => (
              <th
                key={plan}
                scope="col"
                className={`px-3 py-4 font-semibold text-center text-xs sm:text-sm min-w-[140px] align-bottom ${
                  plan.startsWith('Individual') ? 'bg-main-800' : ''
                }`}
              >
                {plan}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {PRICING_MATRIX_ROWS.map((row, rowIndex) => (
            <tr
              key={row.label}
              className={`border-t border-neutral-100 ${rowIndex % 2 === 0 ? 'bg-neutral-25' : 'bg-white'}`}
            >
              <th
                scope="row"
                className={`sticky left-0 z-10 px-4 py-3 ${row.bold ? 'font-bold' : 'font-semibold'} text-main-900 text-xs sm:text-sm border-r border-neutral-100 whitespace-nowrap ${
                  rowIndex % 2 === 0 ? 'bg-neutral-25' : 'bg-white'
                }`}
              >
                {row.label}
              </th>
              {row.kind === 'text'
                ? row.segments.map((seg) => (
                  <td
                    key={`${row.label}-${seg.text}`}
                    colSpan={seg.colSpan}
                    className="px-3 py-3 text-center text-xs sm:text-sm align-top"
                  >
                    {seg.text ? (
                      <span
                        className={
                          seg.highlight
                            ? 'inline-block text-main-900 font-semibold text-xs sm:text-sm leading-snug'
                            : `inline-block text-neutral-600 leading-snug ${row.bold ? 'font-semibold' : ''} ${
                              seg.colSpan > 1 ? 'max-w-none' : 'max-w-[160px]'
                            }`
                        }
                      >
                        {seg.text}
                      </span>
                    ) : (
                      <span className="text-neutral-300">—</span>
                    )}
                  </td>
                ))
                : row.included.map((yes, idx) => (
                  <td
                    key={`${row.label}-${PRICING_PLAN_NAMES[idx]}`}
                    className="px-3 py-3 text-center align-middle"
                  >
                    {yes ? (
                      <span className="inline-flex justify-center text-main-600" aria-label="Included">
                        <CheckIcon className="h-5 w-5" aria-hidden />
                      </span>
                    ) : (
                      <span className="text-neutral-300" aria-label="Not included">
                        —
                      </span>
                    )}
                  </td>
                ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PricingPage() {
  useDocumentTitle('Pricing');

  return (
    <main className="what-is-nucleo-page bg-neutral-50 relative bg-white overflow-hidden">
      <section className="overflow-hidden md:overflow-visible relative flex justify-center pb-8 md:pb-0">
        <LandingNavigation />

        <img
          alt=""
          src={mainScreenBg}
          className="absolute top-0 -right-44 md:right-0 transform 2xl:scale-150 pointer-events-none"
        />

        <Fade left>
          <div className="mt-24 xl:mt-0 z-10 w-full flex items-center min-h-[320px] md:min-h-[400px] max-w-6xl w-full px-4 md:px-6 xl:px-0">
            <div className="backdrop-blur-md w-full bg-white/90 md:w-auto px-8 py-12 rounded-md xl:p-0 xl:bg-transparent xl:backdrop-filter-none">
              <p className="text-main-900 font-extrabold text-4xl sm:text-5xl md:text-5.5xl">
                Pricing
              </p>
              <p className="text-neutral-700 text-base font-medium mt-6" style={{ maxWidth: 530 }}>
                Compare Nucleo Annual Membership Plans (subject to T&amp;Cs).
              </p>
            </div>
          </div>
        </Fade>
      </section>

      <section className="relative pb-16 md:pb-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6 xl:px-0">
          <Fade bottom duration={600}>
            <div className="rounded-md backdrop-blur-md bg-white/90 p-4 md:p-8 overflow-hidden">
              <PricingComparisonTable />
            </div>
          </Fade>
        </div>
      </section>

      <div className="relative overflow-hidden">
        <Footer />
      </div>
    </main>
  );
}
